/**
 * API client.
 *
 * Two headers matter: the bearer token (identity, from Clerk) and
 * X-Garage-Id (which tenant this request acts in). The backend resolves
 * membership and role from those, so the client never asserts a role — it
 * only reports what the server told it.
 */

const BASE = import.meta.env.VITE_API_URL || "/api/v1";

/**
 * Some URLs the API hands back are relative to it, not to the page — a
 * garage's uploaded logo, resolved through a stable proxy path rather than a
 * raw (short-lived) presigned link. A plain external URL, still supported
 * for a garage that pasted one in instead of uploading a file, is left
 * exactly as it is.
 */
export function resolveMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith("/") ? `${BASE}${url}` : url;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 402 — subscription lapsed. Writes blocked, reads still fine. */
  get isBillingBlocked() {
    return this.status === 402;
  }

  get isForbidden() {
    return this.status === 403;
  }
}

type TokenGetter = () => Promise<string | null> | string | null;

let getToken: TokenGetter = () => null;
let activeGarageId: string | null = null;

export function configureAuth(tokenGetter: TokenGetter) {
  getToken = tokenGetter;
}

export function setActiveGarage(garageId: string | null) {
  activeGarageId = garageId;
}

export function getActiveGarage() {
  return activeGarageId;
}

/**
 * Whether the currently configured getter actually has something to offer —
 * the one place that answers "is anybody signed in?" Callers must not
 * re-implement this by peeking at `localStorage` directly: `configureAuth`
 * can point at Clerk's live session instead of localStorage entirely, and a
 * separate hardcoded check would silently stop agreeing with what requests
 * actually send the moment it did.
 */
export async function hasCredential(): Promise<boolean> {
  return Boolean(await getToken());
}

async function request<T>(
  path: string,
  options: RequestInit & { garageId?: string | null } = {},
): Promise<T> {
  const { garageId, ...init } = options;
  const headers = new Headers(init.headers);

  headers.set("Content-Type", "application/json");

  const token = await getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const garage = garageId !== undefined ? garageId : activeGarageId;
  if (garage) headers.set("X-Garage-Id", garage);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = body?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? "error",
      error.message ?? response.statusText,
      error,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, opts?: { garageId?: string | null }) =>
    request<T>(path, { method: "GET", ...opts }),

  post: <T>(path: string, body?: unknown, opts?: { garageId?: string | null }) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      ...opts,
    }),

  patch: <T>(path: string, body?: unknown, opts?: { garageId?: string | null }) =>
    request<T>(path, {
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
      ...opts,
    }),

  put: <T>(path: string, body?: unknown, opts?: { garageId?: string | null }) =>
    request<T>(path, {
      method: "PUT",
      body: body === undefined ? undefined : JSON.stringify(body),
      ...opts,
    }),

  delete: <T>(path: string, opts?: { garageId?: string | null }) =>
    request<T>(path, { method: "DELETE", ...opts }),

  /**
   * Fetch a binary document and hand it to the browser as a download.
   *
   * A plain <a href> can't do this: the endpoint needs the bearer token and
   * the tenant header, and neither survives a navigation. So the bytes come
   * back through fetch and are handed over as an object URL.
   */
  async download(path: string, fallbackName: string): Promise<void> {
    const headers = new Headers();
    const token = await getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (activeGarageId) headers.set("X-Garage-Id", activeGarageId);

    const response = await fetch(`${BASE}${path}`, { headers });
    if (!response.ok) {
      const text = await response.text();
      let message = response.statusText;
      try {
        message = JSON.parse(text)?.error?.message ?? message;
      } catch {
        /* a non-JSON error body is still just a message */
      }
      throw new ApiError(response.status, "download_failed", message);
    }

    // Prefer the filename the server chose — it carries the document number.
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = match?.[1] ?? fallbackName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoking immediately can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
};

/**
 * Public endpoints — tracker and quote portal.
 *
 * Deliberately a separate client with NO auth headers: these are opened by a
 * customer who has no account, and sending a stale token would be both
 * pointless and a small information leak.
 */
export const publicApi = {
  async get<T>(path: string, sessionToken?: string): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (sessionToken) headers["X-Tracker-Session"] = sessionToken;

    const response = await fetch(`${BASE}${path}`, { headers });
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;

    if (!response.ok) {
      const error = body?.error ?? {};
      throw new ApiError(
        response.status,
        error.code ?? "error",
        error.message ?? response.statusText,
        error,
      );
    }
    return body as T;
  },

  async post<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : null;

    if (!response.ok) {
      const error = parsed?.error ?? {};
      throw new ApiError(
        response.status,
        error.code ?? "error",
        error.message ?? response.statusText,
        error,
      );
    }
    return parsed as T;
  },
};
